/**
 * PostHog User Identification Hook
 *
 * Syncs the authenticated user from Zustand auth store to PostHog's identity system.
 * - Calls posthog.identify() when user signs in
 * - Calls posthog.reset() when user signs out
 * - Sets user properties for segmentation and analytics
 */

import { useEffect, useRef } from 'react'
import { usePostHog } from 'posthog-js/react'
import { useAuthStore } from '@/features/auth/stores/authStore'
import type { User } from '@/types/entities'

/**
 * Build user properties for PostHog identify
 */
function buildUserProperties(user: User) {
  return {
    email: user.email,
    name: `${user.firstName} ${user.lastName}`.trim(),
    first_name: user.firstName,
    last_name: user.lastName,
    artistic_name: user.artisticName || null,
    role: user.role,
    verified: user.verified,
    signup_date: user.dateCreate || user.createdAt || null,
    last_login: user.dateLastLogin || null,
  }
}

/**
 * Hook that automatically syncs auth state to PostHog identity
 *
 * Place this hook in PrivateLayout or a similar component that renders
 * for all authenticated routes.
 */
export function usePostHogIdentify() {
  const posthog = usePostHog()
  const user = useAuthStore((state) => state.user)
  const masquerade = useAuthStore((state) => state.masquerade)
  const previousUserId = useRef<number | null>(null)

  useEffect(() => {
    if (!posthog) return
    posthog.register({ role: user?.role || null, is_masquerading: !!masquerade })

    // User signed in or changed
    if (user && user.id !== previousUserId.current) {
      // Use string ID for PostHog distinct_id
      const distinctId = `user_${user.id}`

      posthog.identify(distinctId, buildUserProperties(user))

      // Identification is not proof of a new sign-in.
      // Identification also runs on reload. Genuine password sign-ins are
      // captured by the auth handler; observed sessions by /api/activity.

      previousUserId.current = user.id
    }

    // User signed out
    if (!user && previousUserId.current !== null) {
      posthog.capture('user_signed_out')
      posthog.reset()
      previousUserId.current = null
    }
  }, [posthog, user, masquerade])

  return { isIdentified: !!user }
}

/**
 * Manually identify a user (useful for sign-up flow)
 */
export function identifyUser(posthog: ReturnType<typeof usePostHog>, user: User) {
  if (!posthog) return

  const distinctId = `user_${user.id}`
  posthog.identify(distinctId, buildUserProperties(user))
}

/**
 * Reset PostHog identity (for sign-out)
 */
export function resetIdentity(posthog: ReturnType<typeof usePostHog>) {
  if (!posthog) return
  posthog.reset()
}
